import { Component, effect, input, OnInit, output } from '@angular/core';

import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { VmpFieldType, VmpSelectionType } from '../../enums';
import { ViewModelProperty } from '../../models';

import { TranslatePipe } from '@ngx-translate/core';
import { ButtonEditComponent } from '../../editors/button-edit/button-edit.component';
import { CheckEditComponent } from '../../editors/check-edit/check-edit.component';
import { LookupEditComponent } from '../../editors/lookup-edit/lookup-edit.component';
import { SelectEditComponent } from '../../editors/select-edit/select-edit.component';
import { StaticTextComponent } from '../../editors/static-text/static-text.component';
import { VMPFormGroupItemComponent } from '../vmp-form-group-item/vmp-form-group-item.component';

@Component({
  selector: 'ldw-vmp-form-group',
  templateUrl: './vmp-form-group.component.html',
  styleUrls: ['./vmp-form-group.component.css'],
  imports: [
    MatExpansionPanel,
    MatExpansionPanelHeader,
    ReactiveFormsModule,
    VMPFormGroupItemComponent,
    ButtonEditComponent,
    SelectEditComponent,
    LookupEditComponent,
    CheckEditComponent,
    TranslatePipe,
    StaticTextComponent
  ]
})
export class VMPFormGroupComponent implements OnInit {
  static groupId = 0;

  private isInitialized = false;
  private vmpGroupCaptionText = '';
  private readonly fallbackGroupName = 'GROUP' + VMPFormGroupComponent.groupId++;
  private currentGroupName: string | undefined;

  fieldType = VmpFieldType;
  readonly captionWidth = input<number>(100);
  readonly captionVisible = input<boolean>(true);
  readonly valueWidth = input<number>(400);
  readonly expanded = input<boolean>(false);
  readonly groupCaptionText = input<string>('');
  readonly groupCaptionVisible = input<boolean>(true);
  readonly form = input.required<UntypedFormGroup>();
  readonly vmpList = input<ViewModelProperty[]>([]);

  readonly actionClicked = output<ViewModelProperty>();

  private readonly vmpListChanged = effect(() => {
    this.vmpList();
    if (this.isInitialized) {
      this.buildFormGroup();
    }
  });

  ngOnInit(): void {
    this.isInitialized = true;
    this.buildFormGroup();
  }

  get captionText(): string {
    if (!this.groupCaptionVisible()) {
      return '';
    }
    if (this.groupCaptionText() != '') {
      return this.groupCaptionText();
    }
    return this.vmpGroupCaptionText;
  }

  isSelect(vmp: ViewModelProperty): boolean {
    if (vmp === null) {
      return false;
    }
    return vmp.selectionType === VmpSelectionType.Select;
  }

  isStaticText(vmp: ViewModelProperty): boolean {
    if (vmp === null) {
      return false;
    }
    return vmp.fieldType === VmpFieldType.StaticText;
  }

  isButtonEdit(vmp: ViewModelProperty): boolean {
    if (vmp === null) {
      return false;
    }
    return vmp.selectionType === VmpSelectionType.ButtonEdit;
  }

  buildFormGroup(): void {
    const form = this.form();
    if (!form) {
      return;
    }

    const vmpList = this.vmpList();

    if (vmpList === undefined) {
      return;
    }

    const group: Record<string, UntypedFormControl> = {};
    vmpList.forEach((vmp) => {
      group[vmp.fieldName] = new UntypedFormControl([], vmp.required ? Validators.required : undefined);
    });

    this.getVMPGroupCaptionName();
    const groupName = this.getGroupName();
    if (this.currentGroupName && this.currentGroupName !== groupName && form.contains(this.currentGroupName)) {
      form.removeControl(this.currentGroupName);
    }
    form.setControl(groupName, new UntypedFormGroup(group));
    this.currentGroupName = groupName;
  }

  private getGroupName(): string {
    if (this.vmpGroupCaptionText != '') {
      return this.vmpGroupCaptionText;
    }
    return this.fallbackGroupName;
  }

  private getVMPGroupCaptionName(): void {
    this.vmpGroupCaptionText = '';
    const vmpList = this.vmpList();
    if (vmpList === undefined) {
      return;
    }
    vmpList.forEach((vmp) => {
      if (this.vmpGroupCaptionText === '') {
        if (vmp.subGroupName !== undefined) {
          this.vmpGroupCaptionText = vmp.subGroupName;
        }
      }
    });
  }
}
